import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Input,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import adminApi from "./adminApi";

const { Title, Text } = Typography;

const errorMessageFrom = (error, fallback) =>
  (error.response && error.response.data && error.response.data.message) ||
  fallback;

const eventStatus = (event) => {
  if (!event.ticketTypes || event.ticketTypes.length === 0) {
    return "coming-soon";
  }
  const now = new Date();
  if (event.startDate && now < new Date(event.startDate)) {
    return "coming-soon";
  }
  if (event.endDate && now > new Date(event.endDate)) {
    return "closed";
  }
  return "on-sale";
};

const statusColor = {
  "on-sale": "success",
  "coming-soon": "default",
  closed: "warning",
};

const statusLabel = {
  "on-sale": "On Sale",
  "coming-soon": "Coming Soon",
  closed: "Closed",
};

const formatDate = (date) => (date ? new Date(date).toLocaleDateString() : "-");

export default function EventsPage() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newEventName, setNewEventName] = useState("");
  const [creatingEvent, setCreatingEvent] = useState(false);

  const loadEvents = async () => {
    setLoadingEvents(true);
    try {
      const { data } = await adminApi.get("/api/admin/events");
      setEvents(data);
    } catch (error) {
      message.error("Failed to load events.");
    }
    setLoadingEvents(false);
  };

  useEffect(() => {
    loadEvents();
  }, []);

  const createEvent = async () => {
    if (!newEventName.trim()) return;
    setCreatingEvent(true);
    try {
      const { data } = await adminApi.post("/api/admin/events", {
        name: newEventName.trim(),
      });
      message.success("Event created.");
      setCreateModalOpen(false);
      setNewEventName("");
      navigate(`/admin/events/${data.key}`);
    } catch (error) {
      message.error(errorMessageFrom(error, "Failed to create event."));
    }
    setCreatingEvent(false);
  };

  const deleteEvent = async (event) => {
    try {
      await adminApi.delete(`/api/admin/events/${event.key}`);
      message.success(`${event.name} deleted.`);
      loadEvents();
    } catch (error) {
      message.error(errorMessageFrom(error, "Failed to delete event."));
    }
  };

  const columns = [
    {
      title: "Event",
      dataIndex: "name",
      key: "name",
      render: (name, event) => (
        <Space direction="vertical" size={0}>
          <Text strong>{name}</Text>
          {event.description ? (
            <Text type="secondary" ellipsis style={{ maxWidth: 420 }}>
              {event.description}
            </Text>
          ) : null}
        </Space>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: 130,
      render: (_, event) => {
        const status = eventStatus(event);
        return <Tag color={statusColor[status]}>{statusLabel[status]}</Tag>;
      },
    },
    {
      title: "Tickets",
      key: "tickets",
      width: 90,
      render: (_, event) => event.ticketTypes.length,
    },
    {
      title: "Workbook",
      key: "workbook",
      render: (_, event) => event.workbookName || event.workbookId || "-",
    },
    {
      title: "Start",
      dataIndex: "startDate",
      key: "startDate",
      width: 120,
      render: formatDate,
    },
    {
      title: "End",
      dataIndex: "endDate",
      key: "endDate",
      width: 120,
      render: formatDate,
    },
    {
      title: "",
      key: "actions",
      width: 70,
      render: (_, event) => (
        <Popconfirm
          title={`Delete "${event.name}"? This cannot be undone.`}
          onConfirm={(e) => {
            e?.stopPropagation();
            deleteEvent(event);
          }}
          okText="Delete"
          okButtonProps={{ danger: true }}
        >
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={(e) => e.stopPropagation()}
          />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1rem",
        }}
      >
        <Title level={3} style={{ margin: 0 }}>
          Events
        </Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setCreateModalOpen(true)}
        >
          Add Event
        </Button>
      </div>

      <Table
        rowKey="key"
        loading={loadingEvents}
        columns={columns}
        dataSource={events}
        pagination={false}
        onRow={(event) => ({
          onClick: () => navigate(`/admin/events/${event.key}`),
          style: { cursor: "pointer" },
        })}
      />

      <Modal
        title="Add Event"
        visible={createModalOpen}
        onCancel={() => {
          setCreateModalOpen(false);
          setNewEventName("");
        }}
        onOk={createEvent}
        confirmLoading={creatingEvent}
        okButtonProps={{ disabled: !newEventName.trim() }}
      >
        <Input
          placeholder="Event name (e.g. Winter Social)"
          value={newEventName}
          onChange={(e) => setNewEventName(e.target.value)}
          onPressEnter={createEvent}
          autoFocus
        />
      </Modal>
    </div>
  );
}
